import { useEffect, useState, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { io } from 'socket.io-client';
import { ParsedQs } from 'qs';
import Viewer from 'components/Configurator/Viewer/Viewer';
import SideMenu from 'components/Configurator/Menus/SideMenu';
import Loader from 'components/common/Popup/Loader';
import ErrorPopup from 'components/common/Popup/ErrorPopup';
import { getParamsInventor } from 'utils/parameters';
import Layout from 'components/common/Layout';
import { useUser } from 'contexts';
import AuthOverlay from 'components/Configurator/Configurator/AuthOverlay';
import {
  useFetchSettings,
  useFetchViewable,
  useRunItem,
  useToast,
  useUpdateModelQuoteDownload,
  useVariable,
  useUpdateModelResultDownload,
  useUpdateModelDownload,
  useUnbindEvents,
  useProgressCallback,
} from 'hooks';
import { ThemeContext } from 'contexts/themeContext';
import { socketUrl } from 'constants/sockets';
import { RecoverPasswordQueryParams } from 'components/RecoverPassword/RecoverPassword';
import { specifications } from 'constants/specifications';
import {
  ISettings,
  ConfiguratorQueryParams,
  ActiveParameters,
  IViewerUpdate,
  IData,
  IUpdateModelResultDownload,
  IUpdateModelDownload,
  IUpdateModelError,
  IUpdateModelQuoteDownload,
} from 'types';
import { IParameter, IProductResource, IUserResourceWithRelations } from '../../../../server/types';

export type ConfiguratorProps = {
  queryParams: ParsedQs | null;
};

const Configurator = ({ queryParams }: ConfiguratorProps) => {
  const socketRef = useRef<ReturnType<typeof io>>();
  const [fetchViewable, { loading: fetchViewableLoading, error: fetchViewableError }] = useFetchViewable();
  const [products, setProducts] = useState<IProductResource[]>();
  const [activeParameters, setActiveParameters] = useState<ActiveParameters>();
  const [sortedActiveParameters, setSortedActiveParameters] = useState<ActiveParameters>();
  const [productState, setProductState] = useState<Record<string, never>>();
  const [, setRenderSelectMenu] = useState<boolean>(false);
  const [renderSelectsMenu, setRenderSelectsMenu] = useState<boolean | string | undefined>(false);
  const [valid, setValid] = useState(true);
  const [invalidParams, setInvalidParams] = useState<string[]>();
  const [displayError, setDisplayError] = useState(false);
  const [allowModelDownload, setAllowModelDownload] = useVariable(false);
  const [allowResultDownload, setAllowResultDownload] = useVariable(false);
  const [allowQuoteDownload, setAllowQuoteDownload] = useVariable(false);
  const [allowViewerUpdate, setAllowViewerUpdate] = useVariable(false);
  const [errorMessage, setErrorMessage] = useVariable('');
  const [reportUrl, setReportUrl] = useState('');
  const [, setParameterData] = useState<IParameter[]>({} as IParameter[]);
  const [selectedProduct, setSelectedProduct] = useState<string | number | undefined>('');
  const [disableProcessActions, setDisableProcessActions] = useState(true);
  const [product, setProduct] = useState<IProductResource>();
  const [user] = useUser() as [
    IUserResourceWithRelations,
    React.Dispatch<React.SetStateAction<IUserResourceWithRelations>>
  ];
  const [toggled, setToggled] = useState<boolean>();
  const [flc, setFlc] = useState<string>();

  const [flcEndpointUrl, setFlcEndpointUrl] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [productChanged, setProductChanged] = useState(false);
  const [currentModelParameters, setCurrentModelParameters] = useState<IParameter[]>();
  const [currentJobId, setCurrentJobId] = useVariable<string>();
  const [runItem, { error: runItemError }] = useRunItem();

  const [fetchSettings] = useFetchSettings();
  const [rtlMessage, setRtlMessage] = useState<Record<string, string>>({
    step: 'Request has been queued...',
  });
  const [showToast] = useToast();

  const fetchData = async () => {
    const settingsResult = (await fetchSettings()) as ISettings;

    setToggled(settingsResult?.rfqMode === 'true');
    setFlc(settingsResult?.flcEndpoint);
    setFlcEndpointUrl(settingsResult?.flcEndpointUrl);
  };

  const [generatingDownloads, setGeneratingDownloads] = useState(false);
  const [jobsFinishing, setJobsFinishing] = useState<string[]>([]);

  const [jobsLoading, setJobsLoading] = useState(false);

  const [downloadMenuIcon, setDownloadMenuIcon] = useState(false);

  const [ViewerSvf, setViewerSvf] = useState<string>('');
  const [unbind] = useUnbindEvents();
  const [updateQuoteDownload, { quotePdfUrl, quoteJson }] = useUpdateModelQuoteDownload();
  const [updateModelResult, { outputAssemblyUrl, resultFileNameRef, resultFileJobId }] = useUpdateModelResultDownload();
  const [
    updateModelDownload,
    { drawingPdfUrl, bomJson, bomCsvUrl, exportSatUrl, exportStpUrl, exportDwgUrl, export2dDwgUrl },
  ] = useUpdateModelDownload();
  const [onProgress, { rtlMessage: rtl }] = useProgressCallback();

  const handleUnbindEvents = () => {
    unbind(socketRef);
  };

  const handleOnProgressCallback = async (data: { step: string; progress: number }) => {
    onProgress(data);
    setRtlMessage(rtl);
  };

  const availableSpecifications = specifications.filter((specification) =>
    (user?.role?.specifications ?? []).includes(specification.id)
  );

  const skipDownloading =
    availableSpecifications.length < 1 ||
    (availableSpecifications.length < 2 && availableSpecifications.some(({ id }) => id === 'download-configuration'));

  const isEnabled =
    queryParams?.productAuth !== '1' ||
    !products ||
    !product?.id ||
    !user?.role?.products ||
    user.role.products.includes(product.id);

  const handleCompleteJob = (jobId: string) => {
    setLoading(false);
    setCurrentJobId(jobId);
    setCurrentModelParameters(activeParameters as IParameter[]);
    setDisableProcessActions(false);
  };

  const handleUpdateViewer = (data: IViewerUpdate) => {
    if (data.jobId !== currentJobId || !data.allowUpdate) return;

    const modelPath = `tmp/${data.jobId}viewable/output/1/result.svf`;
    setViewerSvf(modelPath);

    setLoading(false);
    setCurrentJobId(data.jobId);

    setAllowViewerUpdate(false);
  };

  const handleUpdateModelError = (data: IUpdateModelError) => {
    if (allowModelDownload && data.jobId !== currentJobId) return;
    setAllowViewerUpdate(false);
    setAllowModelDownload(false);
    setAllowQuoteDownload(false);
    setAllowResultDownload(false);
    setDisplayError(true);
    setLoading(false);
    setErrorMessage(data.errorMessage as string);
    setReportUrl(data.reportUrl as string);
  };

  const handleUpdateModelQuoteDownload = ({ jobId, results }: IUpdateModelQuoteDownload) => {
    updateQuoteDownload({ jobId, results }, allowQuoteDownload);

    handleCompleteJob(jobId);

    setAllowQuoteDownload(false);
  };

  const handleUpdateModelResultDownload = ({ jobId, results }: IUpdateModelResultDownload) => {
    updateModelResult({ jobId, results }, allowResultDownload);

    handleCompleteJob(jobId);

    setAllowResultDownload(false);
  };

  const handleUpdateModelDownload = async ({ jobId, results }: IUpdateModelDownload) => {
    if (!allowModelDownload || !jobId || jobId !== currentJobId) return;

    updateModelDownload(results);

    handleCompleteJob(jobId);

    setAllowModelDownload(false);
  };

  const updateJobsLoading = () =>
    setJobsLoading(
      (!toggled && !generatingDownloads && jobsFinishing.length < 3) ||
        (toggled && !generatingDownloads && jobsFinishing.length < 2) ||
        (generatingDownloads && jobsFinishing.length < 1)
    );

  useEffect(() => {
    if (!runItemError) return;
    showToast({
      status: 'error',
      message: runItemError,
    });
  }, [runItemError]);

  const handleSocketInit = () => {
    if (!socketRef.current || errorMessage) return;

    handleUnbindEvents();

    socketRef.current?.on('Update Viewer', (data: IViewerUpdate) => {
      handleUpdateViewer(data);
      const modelPath = `tmp/${data.jobId}viewable/output/1/result.svf`;
      setViewerSvf(modelPath);
      jobsFinishing.push('SVF');
      updateJobsLoading();
    });

    socketRef.current?.on('Update Model Error', (data: IUpdateModelError) => {
      handleUpdateModelError(data);
      jobsFinishing.push('Error');
      updateJobsLoading();
    });

    socketRef.current?.on('Update Model Download', (data: IUpdateModelDownload) => {
      handleUpdateModelDownload(data);
      jobsFinishing.push('Downloads');
      updateJobsLoading();
    });

    socketRef.current?.on('realTimeLogging', (data: { step: string; progress: number }) =>
      handleOnProgressCallback(data)
    );

    socketRef.current?.on('Update Model Result Download', (data: IUpdateModelResultDownload) => {
      handleUpdateModelResultDownload(data);
      jobsFinishing.push('Result');
      updateJobsLoading();
    });

    socketRef.current?.on('Update Model Quote Download', async (data: IUpdateModelQuoteDownload) => {
      handleUpdateModelQuoteDownload(data);
      jobsFinishing.push('Quote');
      updateJobsLoading();
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    socketRef.current = io(socketUrl);
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!fetchViewableError) return;
    showToast({
      status: 'error',
      message: fetchViewableError,
    });
  }, [fetchViewableError]);

  useEffect(() => {
    handleSocketInit();
  }, [activeParameters, allowViewerUpdate, allowModelDownload, allowResultDownload, allowQuoteDownload, rtlMessage]);

  const handleParameterUpdateStates = (data: {
    currentActiveParameters: ActiveParameters;
    sentActiveParameters: ActiveParameters;
    currentProduct: IProductResource;
    currentConfigValid: boolean;
    currentInvalidParams: string[];
    currentProductState: {};
  }) => {
    setActiveParameters(data.currentActiveParameters);
    setSortedActiveParameters(data.sentActiveParameters);
    setProduct(data.currentProduct);
    setRenderSelectMenu(true);
    setValid(data.currentConfigValid);
    setInvalidParams(data.currentInvalidParams);

    if (data.currentProductState && Object.keys(data.currentProductState).length !== 0) {
      setProductState(data.currentProductState);
    }
  };

  const handleGetViewable = async (objectName: string, bucketName: string) => {
    const result = await fetchViewable(objectName, bucketName);

    setViewerSvf(result.modelPath);
  };

  const handleUpdateSelectedProductStates = (data: {
    selectedProduct: string | number | undefined;
    product: IProductResource;
    currentUpdateViewer: boolean;
    parameterData?: IParameter[];
  }) => {
    setActiveParameters({});
    setDownloadMenuIcon(false);
    setSelectedProduct(data.selectedProduct);
    setProduct(data.product);
    setParameterData(data.parameterData as IParameter[]);
    setRenderSelectsMenu(true);
    setProductChanged(true);
    setCurrentModelParameters(undefined);
    setCurrentJobId('');
    setLoading(false);

    if (data.currentUpdateViewer) {
      handleGetViewable(data.product.viewable?.objectName as string, data.product.viewable?.bucket as string);
    }
  };

  const handleGetItemState = (data: { newProducts: IProductResource[] }) => {
    setLoading(false);
    setProducts(data.newProducts);
  };

  const updateValue = (dataObject: { optionKey?: string | boolean; renderSelectsMenu?: boolean }) => {
    setRenderSelectsMenu(dataObject.optionKey);
  };

  const showDownloadMenuIcon = (showDownloadIcon = false) => {
    setDownloadMenuIcon(showDownloadIcon);
  };

  const handleRunItem = async (workItem: IData) => {
    const result = await runItem(workItem);
    setLoading(true);
    setCurrentJobId((result as any).jobId);
  };

  // Parameters
  const queueJob = async (download = false, item = 'updateQuote', outputFileNamesActivity = null) => {
    const params = getParamsInventor(sortedActiveParameters!);
    const currentProduct = products?.[selectedProduct as number];
    const outputFilesObj = download ? { inputUrl: outputAssemblyUrl, outputFileNamesActivity } : {};
    const workitem = {
      workitem: item,
      socketId: socketRef.current?.id ?? null,
      user: {
        userName: user ? `${user.firstName} ${user.lastName}` : null,
        userEmail: user?.email ?? null,
      },
      product: currentProduct,
      params,
      version: currentProduct?.version,
      ...outputFilesObj,
    };

    handleRunItem(workitem as IData);

    setJobsLoading(true);
    setGeneratingDownloads(download);
    setJobsFinishing([]);
    setProductChanged(false);
    setErrorMessage('');

    const messageStepParams = download
      ? {
          step_a: 'Creating Downloads.',
        }
      : {};

    setRtlMessage({
      step: 'Request queued...',
      ...messageStepParams,
    } as Record<string, string>);
    setLoading(true);

    setAllowModelDownload(download);
    setAllowResultDownload(!download);
    setAllowQuoteDownload(!download);
    setAllowViewerUpdate(!download);
  };

  const renderLoader = jobsLoading ? <Loader messages={rtlMessage} /> : null;

  const renderError = (
    <ErrorPopup
      message={errorMessage}
      reportUrl={reportUrl}
      isOpen={displayError}
      closePopup={() => setDisplayError(false)}
    />
  );

  const isActive = product && activeParameters;

  const params = isActive ? activeParameters : null;
  const displayName = isActive ? product?.displayName : '';

  const renderViewer = isEnabled ? <Viewer modelPath={ViewerSvf} /> : null;

  return (
    <ThemeContext.Consumer>
      {(theme) => {
        const renderAuthOverlay =
          !user && queryParams?.product ? (
            <AuthOverlay theme={theme} queryParams={queryParams as RecoverPasswordQueryParams} />
          ) : null;

        return (
          <Layout
            queryParams={queryParams as ConfiguratorQueryParams}
            sx={{
              h: '100vh',
              w: '100vw',
              alignItems: 'stretch',
              display: 'flex',
              flex: 'flex-grow',
              flexDir: 'column',
            }}
            testId="configurator-page"
          >
            {renderAuthOverlay}

            <SideMenu
              productDisplayName={displayName as string}
              theme={theme as ISettings}
              queryParamsSideMenu={queryParams as { product: string } & Record<string, never>}
              DownloadMenuIcon={downloadMenuIcon}
              skipDownloading={skipDownloading}
              viewParameters={currentModelParameters}
              products={products as IProductResource[]}
              queryParamsProductMenu={queryParams as { product: string } & Record<string, never>}
              viewerLoading={!!fetchViewableLoading}
              paramsProcessMenu={params! as ActiveParameters & IParameter[]}
              modelDownloadUrl={outputAssemblyUrl}
              modelParameters={currentModelParameters}
              drawingDownloadUrl={drawingPdfUrl}
              downloadExportSat={exportSatUrl}
              downloadExportStp={exportStpUrl}
              downloadExportDwg={exportDwgUrl}
              downloadExport2dDwg={export2dDwgUrl}
              disabled={disableProcessActions}
              product={isEnabled ? (product as IProductResource) : undefined}
              rfq={toggled}
              resultFileJobId={resultFileJobId as string}
              selectedProduct={selectedProduct as string}
              resultFileNameRef={resultFileNameRef as string}
              quote={quoteJson}
              quotePdfUrl={quotePdfUrl as string}
              flc={flc}
              flcUrl={flcEndpointUrl}
              bom={bomJson}
              bomCsvUrl={bomCsvUrl}
              authUser={user}
              paramsOptions={params as IParameter[]}
              productState={productState}
              updateValue={updateValue}
              queueJob={queueJob}
              renderSelectsMenu={renderSelectsMenu as boolean}
              valid={valid}
              invalidParams={invalidParams as string[]}
              loading={fetchViewableLoading || loading || jobsLoading}
              showDownloadMenuIcon={showDownloadMenuIcon}
              jobsLoading={jobsLoading}
              errorExisted={!!errorMessage}
              productChanged={productChanged}
              isEnabled={isEnabled}
              handleGetItemState={handleGetItemState}
              handleUpdateSelectedProductStates={handleUpdateSelectedProductStates}
              handleParameterUpdateStates={handleParameterUpdateStates}
              activeParameters={activeParameters as ActiveParameters}
            />
            <Flex position="relative" alignContent="stretch" width="100%" zIndex={1}>
              {renderError}
              {renderLoader}
              {renderViewer}
            </Flex>
          </Layout>
        );
      }}
    </ThemeContext.Consumer>
  );
};

export default Configurator;
